import { AddIcon } from "@chakra-ui/icons";
import { Button, Flex, Heading, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LearningPathForm from "../../components/forms/LearningPathForm";
import LessonsTable from "../../components/tables/LessonsTable";
import useGetLearningPathDetails from "../../hooks/api/learning-path/useGetLearningPathDetails";
import BasePage from "../common/BasePage";

type Params = {
  learningPathId: string;
};

export default function EditLearningPathPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { learningPathId } = useParams<Params>();
  const {
    data: learningPath,
    isLoading,
    isError,
    refetch: refetchLearningPath,
  } = useGetLearningPathDetails(learningPathId ?? "");

  const onSuccess = useCallback(() => {
    navigate("./..");
    toast({
      title: t("edit_learning_path_page.learning_path_updated"),
      description: t("edit_learning_path_page.learning_path_updated_message"),
      status: "success",
      isClosable: true,
    });
  }, [navigate, t, toast]);

  const onError = useCallback(() => {
    toast({
      title: t("edit_learning_path_page.learning_path_error"),
      description: t("edit_learning_path_page.learning_path_error_message"),
      status: "error",
      isClosable: true,
    });
  }, [t, toast]);

  const goToCreateLesson = useCallback(() => {
    navigate("./lessons/create");
  }, [navigate]);

  return (
    <BasePage
      title={t("edit_learning_path_page.edit_learning_path")}
      isLoading={isLoading}
      hasError={isError}
      retry={refetchLearningPath}
    >
      <Flex gap={16} flexDirection="column">
        <LearningPathForm
          learningPath={learningPath}
          onSuccess={onSuccess}
          onError={onError}
        />
        <div>
          <Flex justifyContent="space-between">
            <Heading size="lg" as="h3" marginBottom={8}>
              {t("edit_learning_path_page.lessons")}
            </Heading>
            <Button
              bg="primary.DEFAULT"
              color="white"
              leftIcon={<AddIcon />}
              onClick={goToCreateLesson}
            >
              {t("common.buttons.new2")}
            </Button>
          </Flex>
          <LessonsTable learningPathId={learningPathId!} />
        </div>
      </Flex>
    </BasePage>
  );
}
